import { fixedUi } from '@/i18n/fixedUi';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import CategoryCascader from '@/temu-platform/components/CategoryCascader';
import { flattenLeafCategories, getCategoryRoots, isFullerCategoryPath } from '@/temu-platform/utils/temuCategoryTree';
import {
  createTemuCategoryChildrenLoader,
  createTemuOfficialCategoryChildrenLoader,
} from '@/temu-platform/utils/temuCategoryLoader';

const CASCADER_COLUMN_WIDTH = 220;
const CASCADER_PANEL_GUTTER = 16;

/**
 * 发品/模板用类目下拉：展开为多列级联；输入关键字时按完整路径过滤末级类目。
 * includeAllOption：面板顶部可选「全部类目」（自动规则等场景）。
 * shopId / loadChildren：非叶子按需拉取下级（Temu 全量类目过大，店铺只缓存一级）。
 */
export default function CategoryCascaderSelect({
  categories = [],
  value = '',
  label = '',
  onChange,
  placeholder = fixedUi("请选择或搜索类目"),
  disabled = false,
  allowClear = true,
  emptyText = fixedUi("暂无可发商品分类"),
  className = '',
  includeAllOption = false,
  allOptionValue = '*',
  allOptionLabel = fixedUi("全部类目"),
  shopId,
  officialShopType,
  loadChildren: loadChildrenProp,
}) {
  const loadChildren = useMemo(() => {
    if (typeof loadChildrenProp === 'function') return loadChildrenProp;
    if (shopId) return createTemuCategoryChildrenLoader(shopId);
    // 管理端「官方模板」弹窗没有 shopId：类目缓存只存一级，需借同类型店铺按需拉下级类目
    if (officialShopType) return createTemuOfficialCategoryChildrenLoader(officialShopType);
    return undefined;
  }, [loadChildrenProp, shopId, officialShopType]);
  const rootRef = useRef(null);
  const controlRef = useRef(null);
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [panelStyle, setPanelStyle] = useState({});
  const [blockAutofill, setBlockAutofill] = useState(true);
  const [columnCount, setColumnCount] = useState(1);

  const leafOptions = useMemo(() => flattenLeafCategories(categories), [categories]);
  // 店铺同步只缓存一级非叶子时 leafOptions 为空，但仍应展示级联 + 懒加载下级
  const rootNodes = useMemo(() => getCategoryRoots(categories), [categories]);
  const hasBrowsableTree = rootNodes.length > 0;
  const isAllSelected = includeAllOption && String(value || '') === String(allOptionValue);
  const selected = useMemo(
    () => leafOptions.find((opt) => String(opt.categoryId) === String(value)) || null,
    [leafOptions, value],
  );
  const displayLabel = isAllSelected
    ? allOptionLabel
    : (isFullerCategoryPath(label, selected?.label) ? String(label || '').trim() : (selected?.label || label || ''));

  const filtered = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return [];
    const leaves = leafOptions.filter((opt) => String(opt.label || '').toLowerCase().includes(q));
    if (includeAllOption && String(allOptionLabel).toLowerCase().includes(q)) {
      return [{ categoryId: allOptionValue, label: allOptionLabel }, ...leaves];
    }
    return leaves;
  }, [leafOptions, keyword, includeAllOption, allOptionValue, allOptionLabel]);

  const showingSearch = keyword.trim().length > 0;

  const updatePanelPosition = () => {
    const el = controlRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const view = window.visualViewport;
    const viewW = view?.width ?? window.innerWidth;
    const viewH = view?.height ?? window.innerHeight;
    const viewLeft = view?.offsetLeft ?? 0;
    const viewTop = view?.offsetTop ?? 0;
    const viewRight = viewLeft + viewW;
    const viewBottom = viewTop + viewH;
    const maxWidth = Math.max(160, viewW - CASCADER_PANEL_GUTTER);
    const cascaderWidth = Math.max(
      rect.width,
      Math.max(1, columnCount) * CASCADER_COLUMN_WIDTH + 2,
    );
    const width = Math.min(
      showingSearch ? Math.max(rect.width, 320) : cascaderWidth,
      maxWidth,
    );
    let left = rect.left;
    if (left + width > viewRight - 8) {
      left = Math.max(viewLeft + 8, viewRight - 8 - width);
    }
    const spaceBelow = viewBottom - rect.bottom - 12;
    const spaceAbove = rect.top - viewTop - 12;
    const minComfort = 240;
    const preferBelow = spaceBelow >= minComfort || spaceBelow >= spaceAbove;
    const maxHeight = Math.min(360, Math.max(140, preferBelow ? spaceBelow : spaceAbove));
    setPanelStyle({
      position: 'fixed',
      top: preferBelow ? rect.bottom + 4 : undefined,
      bottom: preferBelow ? undefined : Math.max(8, viewH - (rect.top - viewTop) + 4),
      left,
      width,
      maxWidth,
      maxHeight,
      // 级联模式给固定高度，否则列 max-height:100% 无法生效，二级类目无法纵向滚动
      height: showingSearch ? undefined : maxHeight,
      overflowX: showingSearch ? 'hidden' : 'auto',
      overflowY: showingSearch ? 'auto' : 'hidden',
      zIndex: 13000,
    });
  };

  useLayoutEffect(() => {
    if (!open) return undefined;
    updatePanelPosition();
    const onReposition = () => updatePanelPosition();
    window.addEventListener('resize', onReposition);
    window.addEventListener('scroll', onReposition, true);
    window.visualViewport?.addEventListener('resize', onReposition);
    window.visualViewport?.addEventListener('scroll', onReposition);
    return () => {
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition, true);
      window.visualViewport?.removeEventListener('resize', onReposition);
      window.visualViewport?.removeEventListener('scroll', onReposition);
    };
  }, [open, showingSearch, filtered.length, categories, columnCount]);

  useLayoutEffect(() => {
    if (!open || showingSearch) return;
    const panel = panelRef.current;
    if (!panel) return;
    if (panel.scrollWidth > panel.clientWidth + 1) {
      panel.scrollLeft = panel.scrollWidth;
    }
  }, [open, showingSearch, columnCount, panelStyle.width]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      const inRoot = rootRef.current?.contains(event.target);
      const inPanel = panelRef.current?.contains(event.target);
      if (!inRoot && !inPanel) {
        setOpen(false);
        setKeyword('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const pick = (categoryId, meta) => {
    const next = categoryId == null || categoryId === '' ? '' : String(categoryId);
    setOpen(false);
    setKeyword('');
    if (String(value || '') === next) return;
    onChange?.(next, meta || null);
  };

  const displayValue = open ? keyword : displayLabel;

  const panel = open && !disabled ? createPortal(
    <div
      ref={panelRef}
      className={`tpo-search-select__panel tpo-search-select__panel--portal tpo-category-cascader-select__panel${showingSearch ? ' is-search' : ''}`}
      style={panelStyle}
      data-testid="temu-category-cascader-select-panel"
      onMouseDown={(e) => e.preventDefault()}
    >
      {showingSearch ? (
        filtered.length === 0 ? (
          <div className="tpo-search-select__empty">{fixedUi("无匹配类目")}</div>
        ) : filtered.map((opt) => {
          const active = String(opt.categoryId) === String(value);
          return (
            <button
              key={opt.categoryId}
              type="button"
              className={`tpo-search-select__option${active ? ' is-on' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(opt.categoryId)}
            >
              {opt.label}
            </button>
          );
        })
      ) : !hasBrowsableTree && !includeAllOption ? (
        <div className="tpo-search-select__empty">{emptyText}</div>
      ) : (
        <>
          {includeAllOption ? (
            <button
              type="button"
              className={`tpo-search-select__option tpo-category-cascader-select__all${isAllSelected ? ' is-on' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(allOptionValue)}
            >
              {allOptionLabel}
            </button>
          ) : null}
          {hasBrowsableTree ? (
            <CategoryCascader
              categories={categories}
              selectedCategoryId={isAllSelected ? '' : value}
              hideDetail
              loadChildren={loadChildren}
              onColumnsChange={setColumnCount}
              onChange={(payload) => {
                if (!payload?.categoryId) return;
                pick(String(payload.categoryId), {
                  categoryId: String(payload.categoryId),
                  productTypeId: payload.productTypeId != null ? String(payload.productTypeId) : String(payload.categoryId),
                  label: payload.categoryPathLabel || '',
                  pathIds: Array.isArray(payload.pathIds) ? payload.pathIds.map(String) : [],
                });
              }}
            />
          ) : (
            <div className="tpo-search-select__empty">{emptyText}</div>
          )}
        </>
      )}
    </div>,
    document.body,
  ) : null;

  const openPanel = () => {
    if (disabled) return;
    if (blockAutofill) setBlockAutofill(false);
    setOpen(true);
    setKeyword('');
  };

  return (
    <div
      className={`tpo-search-select tpo-category-cascader-select${open ? ' is-open' : ''}${disabled ? ' is-disabled' : ''}${className ? ` ${className}` : ''}`}
      ref={rootRef}
      data-testid="temu-category-cascader-select"
    >
      <div
        className="tpo-search-select__control"
        ref={controlRef}
        onMouseDown={(e) => {
          if (disabled) return;
          if (e.button !== 0) return;
          if (e.target?.closest?.('.tpo-search-select__clear')) return;
          openPanel();
        }}
      >
        <input
          ref={inputRef}
          type="text"
          className="tpo-input tpo-search-select__input"
          value={displayValue}
          disabled={disabled}
          placeholder={displayLabel || placeholder}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={open}
          data-lpignore="true"
          data-1p-ignore="true"
          data-bwignore="true"
          data-form-type="other"
          readOnly={blockAutofill}
          onFocus={() => {
            openPanel();
          }}
          onBlur={() => {
            setBlockAutofill(true);
          }}
          onChange={(e) => {
            setKeyword(e.target.value);
            setOpen(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              const first = filtered[0];
              if (first) pick(first.categoryId);
            }
            if (e.key === 'Escape') {
              setOpen(false);
              setKeyword('');
            }
            if (e.key === 'Backspace' && !keyword && allowClear && value) {
              onChange?.('');
            }
          }}
        />
        {allowClear && value && !disabled ? (
          <button
            type="button"
            className="tpo-search-select__clear"
            aria-label={fixedUi("清除")}
            onClick={() => {
              onChange?.('');
              setKeyword('');
              inputRef.current?.focus();
            }}
          >
            ×
          </button>
        ) : (
          <span className="tpo-search-select__caret" aria-hidden>▾</span>
        )}
      </div>
      {panel}
    </div>
  );
}
